import { inject, Injectable } from '@angular/core';
import { OAuthCredential } from 'mbs-ui-shared@models';
import { AuthService } from 'mbs-ui-shared@services/auth/auth.service';
import { BehaviorSubject, noop, Observable } from 'rxjs';

const PENDING_LOGIN = 'pending';

@Injectable({
  providedIn: 'root'
})
export class OneTimeSessionService {
  private readonly credentialsStorageKey = 'oneTimeSessionTokenData';
  private readonly activeStorageKey = 'oneTimeSessionActive';

  private authService = inject(AuthService);

  private credentialsInternal$ = new BehaviorSubject<OAuthCredential>(this.readCredentials());

  constructor() {
    window.addEventListener('pagehide', () => this.end());
  }

  public get credentials$(): Observable<OAuthCredential> {
    return this.credentialsInternal$.asObservable();
  }

  public get credentials(): OAuthCredential {
    return this.credentialsInternal$.value;
  }

  public set credentials(value: OAuthCredential) {
    value ? sessionStorage.setItem(this.credentialsStorageKey, JSON.stringify(value)) : this.removeCredentials();

    this.credentialsInternal$.next(value ?? null);
  }

  public get authToken(): string {
    const credentials = this.credentials;

    return credentials ? `${credentials.token_type} ${credentials.access_token}` : '';
  }

  public get isActive(): boolean {
    return Boolean(this.marker) || Boolean(this.credentials);
  }

  public begin(): void {
    this.end();
    sessionStorage.setItem(this.activeStorageKey, PENDING_LOGIN);
  }

  public awaitTwoFactorAuth(authId: string): void {
    sessionStorage.setItem(this.activeStorageKey, authId);
  }

  public isAwaitedTwoFactorAuth(authId: string): boolean {
    return Boolean(authId) && this.marker === authId;
  }

  public end(onlyLogin?: boolean): void {
    if (!this.isActive) return;

    const authToken = this.authToken;

    try {
      if (authToken && !onlyLogin) this.authService.logoutOneTimeSession(authToken).subscribe({ error: noop });
    } finally {
      sessionStorage.removeItem(this.activeStorageKey);
      this.removeCredentials();
      this.credentialsInternal$.next(null);
    }
  }

  private get marker(): string {
    return sessionStorage.getItem(this.activeStorageKey);
  }

  private removeCredentials(): void {
    sessionStorage.removeItem(this.credentialsStorageKey);
  }

  private readCredentials(): OAuthCredential {
    try {
      return JSON.parse(sessionStorage.getItem(this.credentialsStorageKey));
    } catch {
      this.removeCredentials();

      return null;
    }
  }
}
